import { Box, Button, Card, CardMedia, Pagination, Grid, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useHistory } from 'react-router';
import { CommonLayout } from '../commonComponents';
import { useDispatch, useSelector } from 'react-redux';
import { DISABLE_NEW_APPLICATIONS, USER_ROLES } from '../../utils/constant';
import { RootStateType } from '../../redux-store/reducers';
import TopDistributorRM from './topDistributorsRM';
import CommitmentAndContribution from './commitmentAndContribution';
import DashboardAndApplicationsList from './applicationslist';
import { useEffect, useState } from 'react';
import { ApplicationProps } from '../../redux-store/types/api-types';
import { getAllApplications, getWeeklyGrowth } from '../../redux-store/actions/application';
import {
  GetAllApplicantionsResponseBody,
  WeeklyGrowthResponse,
} from '../../redux-store/types/application';
import OnboardingdDashboard from './OnboardingDashboard';

const PAGE_LIMIT = 5;

export default function Dashboard(): JSX.Element {
  const history = useHistory();
  const [isLoading, setLoading] = useState(false);
  const { role = '' } = useSelector((store: RootStateType) => store.auth);
  const [applicationsList, setApplicationsList] = useState<ApplicationProps[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [growthPercent, setGrowthPercent] = useState<number | null>(null);
  const dispatch = useDispatch();

  useEffect(() => {
    let componentIsAlive = true;
    setLoading(true);
    (async function () {
      try {
        const response = (await dispatch(
          getAllApplications({
            page,
            limit: PAGE_LIMIT,
          })
        )) as unknown as GetAllApplicantionsResponseBody;
        const { applications, pages } = response || {};
        if (!componentIsAlive) {
          return;
        }
        setApplicationsList(applications || []);
        setTotalPages(pages || 1);
      } catch (e) {
        console.error((e as Error).message);
      } finally {
        if (componentIsAlive) {
          setLoading(false);
        }
      }
    })();
    return () => {
      componentIsAlive = false;
    };
  }, [page]);

  useEffect(() => {
    let componentIsAlive = true;
    (async function () {
      try {
        const response = (await dispatch(getWeeklyGrowth())) as unknown as WeeklyGrowthResponse;
        if (!componentIsAlive) {
          return;
        }
        setGrowthPercent(
          typeof response?.growthPercent === 'number' ? response.growthPercent : null
        );
      } catch (e) {
        console.error((e as Error).message);
      }
    })();
    return () => {
      componentIsAlive = false;
    };
  }, []);

  const showGrowthBadge = growthPercent != null && growthPercent >= 0;
  const isPositiveGrowth = (growthPercent ?? 0) >= 0;

  return (
    <CommonLayout>
      <Box
        sx={{
          fontFamily: 'Roboto, sans-serif',
          width: '100%',
          minWidth: 0,
          maxWidth: '100%',
          overflowX: 'hidden',
          '& .MuiTypography-root, & .MuiTypography-h6, & .MuiTypography-body1, & .MuiButton-root, & .MuiInputBase-root, & .MuiInputBase-input, & .MuiTableCell-root, & .MuiChip-root, & .MuiChip-label, & .MuiLink-root, & .MuiTab-root, & .MuiMenuItem-root, & .MuiFormLabel-root, & .MuiSelect-select, & .MuiPaginationItem-root, & .MuiFormControlLabel-label, & .MuiListItemText-primary, & .MuiGrid-root':
            {
              fontFamily: 'Roboto, sans-serif !important',
            },
        }}>
        <Grid container spacing={{ xs: 1.5, sm: 2.5 }} sx={{ width: '100%', margin: 0 }}>
          <OnboardingdDashboard />
        </Grid>

        <Grid container spacing={{ xs: 1.5, sm: 2.5 }} py={{ xs: 2, sm: 3 }} sx={{ width: '100%', margin: 0 }}>
          {USER_ROLES.AMC_ADMIN === role && <TopDistributorRM />}
          <CommitmentAndContribution fullWidth={USER_ROLES.AMC_ADMIN !== role} />
        </Grid>

        <Card
          elevation={0}
          sx={{
            p: 2.5,
            borderRadius: 3,
            border: '1px solid #E8ECF0',
            background: '#fff',
            fontFamily: 'Roboto, sans-serif',
            minWidth: 0,
            overflow: 'hidden',
          }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              flexWrap: 'wrap',
              mb: 2.5,
            }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontStyle: 'normal',
                  fontSize: 20,
                  fontWeight: 600,
                  lineHeight: 1,
                  letterSpacing: '0.01em',
                  color: '#111927',
                }}
                component="h2">
                Recent Applications
              </Typography>
              {showGrowthBadge && (
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.75,
                    px: 1.25,
                    py: 0.5,
                    borderRadius: 2,
                    background: isPositiveGrowth ? '#EEF2FF' : '#FEF2F2',
                    color: isPositiveGrowth ? '#006EB7' : '#B42318',
                  }}>
                  {isPositiveGrowth && (
                    <CardMedia
                      component="img"
                      src="/images/growth-icon.svg"
                      alt=""
                      aria-hidden="true"
                      sx={{ width: 16, height: 16 }}
                    />
                  )}
                  <Typography
                    sx={{
                      fontFamily: 'Roboto, sans-serif',
                      fontWeight: 600,
                      fontStyle: 'normal',
                      fontSize: '12px',
                      lineHeight: '16px',
                      letterSpacing: 0,
                      color: isPositiveGrowth ? '#006EB7' : '#B42318',
                    }}>
                    {isPositiveGrowth ? '+' : ''}
                    {growthPercent}% this week
                  </Typography>
                </Box>
              )}
            </Box>
            {[
              USER_ROLES.AMC_ADMIN,
              USER_ROLES.RM,
              USER_ROLES.DISTRIBUTOR,
              USER_ROLES.SUBDISTRIBUTOR,
            ].includes(role) && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                aria-label="Add Application"
                sx={{
                  fontFamily: 'Roboto, sans-serif',
                  background: '#2057A6',
                  color: '#FFFFFF',
                  fontWeight: 500,
                  fontSize: 14,
                  textTransform: 'none',
                  borderRadius: 2,
                  px: 2,
                  py: 1,
                  boxShadow: 'none',
                  whiteSpace: 'nowrap',
                  '&:hover': {
                    background: '#1A4A8C',
                    boxShadow: 'none',
                  },
                }}
                onClick={() => history.push('/onboarding')}>
                Add Application
              </Button>
            )}
          </Box>

            <Box sx={{ maxWidth: { lg: '100%' }, minWidth: 0, width: '100%' }}>
            <DashboardAndApplicationsList
              isLoading={isLoading}
              applicationsList={applicationsList}
              boxStyles={{
                minHeight: 'auto',
                maxHeight: 'none',
                overflowY: 'visible',
                width: '100%',
                overflowX: 'auto',
              }}
              DashboardApplicationsList={true}
            />
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              flexWrap: 'wrap',
              pt: 2.5,
            }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_event, value) => setPage(value)}
              shape="rounded"
              siblingCount={1}
              boundaryCount={1}
              sx={{
                '& .MuiPaginationItem-root': {
                  fontFamily: 'Roboto, sans-serif',
                  fontWeight: 500,
                  fontSize: 12,
                  lineHeight: 1,
                  letterSpacing: 0,
                  textAlign: 'center',
                  color: '#111927',
                  minWidth: 26,
                  height: 26,
                },
                '& .Mui-selected': {
                  backgroundColor: '#111927 !important',
                  color: '#FFFFFF !important',
                  borderRadius: '50%',
                },
              }}
            />
            <Box
              component="button"
              type="button"
              onClick={() => history.push('/applications')}
              sx={{
                display: 'flex',
                alignItems: 'center',
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                color: '#006EB7',
                fontSize: 14,
                fontWeight: 600,
                fontFamily: 'Roboto, sans-serif',
                p: 0,
              }}>
              View All
              <ChevronRightIcon sx={{ fontSize: 18 }} />
            </Box>
          </Box>
        </Card>
      </Box>
    </CommonLayout>
  );
}
